import React from "react";
import { useLocation } from "react-router-dom";
import { HiArrowPath, HiExclamationTriangle } from "react-icons/hi2";

// Barreira de erro de render.
//
// Sem ela, um único erro de render — um payload de mensagem em formato
// inesperado, uma mídia estranha, um null onde se esperava objeto — desmonta a
// árvore inteira e o usuário fica com a tela branca no meio do atendimento, sem
// nem saber o que aconteceu. React não tem equivalente em hook: capturar erro de
// render exige componente de classe, e é por isso que este arquivo tem um.
//
// `resetKey`: quando muda, a barreira volta a renderizar os filhos. É o que faz
// navegar para outra rota "limpar" o erro — sem isso, uma vez quebrada a
// barreira ficaria quebrada até um F5.
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
    this.handleRetry = this.handleRetry.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidUpdate(prevProps) {
    if (
      this.state.error &&
      prevProps.resetKey !== this.props.resetKey
    ) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ error: null });
    }
  }

  componentDidCatch(error, info) {
    // O console é o que sobra para o suporte reconstruir o caso: o usuário
    // relata "ficou branco" e sem isto não há nem a mensagem do erro.
    console.error(
      `[ErrorBoundary${this.props.label ? ` ${this.props.label}` : ""}]`,
      error,
      info?.componentStack,
    );
    if (typeof this.props.onError === "function") {
      try {
        this.props.onError(error, info);
      } catch {
        // uma falha ao reportar não pode virar um segundo erro na tela
      }
    }
  }

  handleRetry() {
    this.setState({ error: null });
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    if (typeof this.props.fallback === "function") {
      return this.props.fallback({ error, retry: this.handleRetry });
    }
    if (this.props.fallback) return this.props.fallback;

    return (
      <ErrorBoundaryFallback
        error={error}
        onRetry={this.handleRetry}
        title={this.props.title}
        description={this.props.description}
        compact={this.props.compact}
      />
    );
  }
}

// Tela padrão do erro. Fica separada do componente de classe para poder usar
// hooks/estilos normalmente e para os testes montarem só ela.
export const ErrorBoundaryFallback = ({
  error,
  onRetry,
  title,
  description,
  compact = false,
}) => (
  <div
    role="alert"
    className={[
      "flex flex-col items-center justify-center gap-3 text-center",
      compact ? "p-6" : "min-h-[60vh] p-8",
    ].join(" ")}
  >
    <span className="flex h-12 w-12 items-center justify-center rounded-full bg-amber-50">
      <HiExclamationTriangle className="h-6 w-6 text-amber-500" />
    </span>
    <p className="text-base font-semibold text-slate-800">
      {title || "Algo quebrou por aqui"}
    </p>
    <p className="max-w-md text-sm leading-6 text-slate-500">
      {description ||
        "A tela não conseguiu ser desenhada. Seus dados não foram perdidos — tente de novo, e se continuar acontecendo avise o suporte."}
    </p>
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={onRetry}
        className="inline-flex items-center gap-2 rounded-lg bg-[#e45412] px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#c9490f] dark:hover:bg-[#FF6A21]"
      >
        <HiArrowPath className="h-4 w-4" />
        Tentar de novo
      </button>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50"
      >
        Recarregar a página
      </button>
    </div>
    {error?.message && (
      <details className="mt-2 max-w-lg text-left">
        <summary className="cursor-pointer text-xs text-slate-400 hover:text-slate-600">
          Detalhes técnicos
        </summary>
        <pre className="mt-2 max-h-40 overflow-auto rounded-lg bg-slate-50 p-2 text-[11px] leading-4 text-slate-500">
          {String(error.message)}
        </pre>
      </details>
    )}
  </div>
);

// Barreira de rota: a chave é o pathname, então sair da tela quebrada já
// restaura a barreira — o usuário não fica preso até recarregar.
export const RouteErrorBoundary = ({ children }) => {
  const location = useLocation();
  return (
    <ErrorBoundary resetKey={location.pathname} label={location.pathname}>
      {children}
    </ErrorBoundary>
  );
};

export default ErrorBoundary;
